import { useState } from 'react'
import { useAuth } from '../state/auth'
import { useTours } from '../state/tours'
import { formatKm } from '../lib/geo'
import { isIos, isStandalone } from '../lib/platform'

export default function ProfilePanel() {
  const auth = useAuth()
  const tours = useTours((s) => s.tours)
  const sync = useTours((s) => s.sync)
  const syncing = useTours((s) => s.syncing)
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')

  const totalKm = tours.filter((t) => t.kind === 'recorded').reduce((a, t) => a + t.data.stats.distanceM, 0)
  const totalUp = tours.filter((t) => t.kind === 'recorded').reduce((a, t) => a + t.data.stats.ascentM, 0)

  const submit = async () => {
    const ok =
      mode === 'login'
        ? await auth.login(email.trim(), password)
        : await auth.register(email.trim(), name, password)
    if (ok) {
      setPassword('')
      void sync()
    }
  }

  return (
    <>
      <h2>Profile</h2>

      {auth.status === 'in' && auth.user ? (
        <>
          <div className="hint tinted">
            Signed in as <b>{auth.user.name}</b> ({auth.user.email}). Your tours sync to the cloud automatically.
          </div>
          <div className="shell">
            <div className="core">
              <div className="stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="stat">
                  <div className="v">{tours.length}</div>
                  <div className="l">Tours</div>
                </div>
                <div className="stat">
                  <div className="v">{formatKm(totalKm)}</div>
                  <div className="l">Recorded</div>
                </div>
                <div className="stat">
                  <div className="v">{Math.round(totalUp)} m</div>
                  <div className="l">Climbed</div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <button className="btn" style={{ flex: 1 }} onClick={() => void sync()} disabled={syncing}>
              {syncing ? 'Syncing…' : '↻ Sync now'}
            </button>
            <button className="btn danger" onClick={() => void auth.logout()}>
              Sign out
            </button>
          </div>
        </>
      ) : (
        <div className="auth-box">
          <div className="seg" style={{ marginBottom: 12 }}>
            <button className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>
              Sign in
            </button>
            <button className={mode === 'register' ? 'active' : ''} onClick={() => setMode('register')}>
              Create account
            </button>
          </div>
          {mode === 'register' && (
            <input className="field" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
          )}
          <input
            className="field"
            type="email"
            placeholder="Email"
            value={email}
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            className="field"
            type="password"
            placeholder={mode === 'register' ? 'Password (min. 8 characters)' : 'Password'}
            value={password}
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void submit()}
          />
          {auth.error && <p className="error-text">{auth.error}</p>}
          <button className="btn primary block" onClick={() => void submit()} disabled={auth.busy}>
            {auth.busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
          <p className="muted" style={{ marginTop: 10 }}>
            An account backs up your tours and syncs them across devices. The app works fully without one — tours then
            stay on this device.
          </p>
        </div>
      )}

      {isIos() && !isStandalone() && (
        <>
          <span className="eyebrow">Install on your iPhone</span>
          <div className="hint">
            Open this site in <b>Safari</b>, tap the <b>Share</b> button, then <b>“Add to Home Screen”</b>. Traverse
            runs full-screen with GPS, works offline, and keeps your recordings safe. Do offline map downloads from the
            installed app.
          </div>
        </>
      )}

      <span className="eyebrow">About</span>
      <p className="muted" style={{ lineHeight: 1.55 }}>
        Traverse — route planning, navigation and tour recording for hiking, cycling and running. Maps ©
        OpenStreetMap contributors via OpenFreeMap · Terrain by Tilezen/AWS Open Data · Routing by FOSSGIS Valhalla ·
        Places by Photon &amp; Overpass · Weather by Open-Meteo (CC-BY 4.0). Community servers — please be
        considerate.
      </p>
    </>
  )
}
